<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { apiLogger, type LogEntry, LogLevel } from '../lib/api-logger';
  import { t } from '../lib/i18n';
  
  // State
  let logs: LogEntry[] = [];
  let filter: LogLevel | 'ALL' = 'ALL';
  let endpointFilter: string = '';
  let autoRefresh = true;
  let refreshInterval: number | undefined;
  let selectedLog: LogEntry | null = null;
  let showDetailsModal = false;
  let searchTerm = '';
  
  // Update logs
  function updateLogs() {
    logs = apiLogger.getLogs();
  }
  
  // Clear logs
  function clearLogs() {
    if (confirm(t('Are you sure you want to clear all logs?'))) {
      apiLogger.clearLogs();
      updateLogs();
    }
  }
  
  // Filter logs by level and endpoint
  function getFilteredLogs() {
    let filtered = logs;
    
    // Apply level filter
    if (filter !== 'ALL') {
      filtered = filtered.filter(log => log.level === filter);
    }
    
    // Apply endpoint filter if provided
    if (endpointFilter) {
      filtered = filtered.filter(log => {
        const url = log.data?.url || '';
        return url.includes(endpointFilter);
      });
    }
    
    // Apply search filter if provided
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log => {
        const message = log.message.toLowerCase();
        const url = (log.data?.url || '').toLowerCase();
        const method = (log.data?.method || '').toLowerCase();
        const status = String(log.data?.status || '');
        
        return message.includes(term) || 
               url.includes(term) || 
               method.includes(term) || 
               status.includes(term);
      });
    }
    
    return filtered;
  }
  
  // Toggle auto refresh
  function toggleAutoRefresh() {
    autoRefresh = !autoRefresh;
    if (autoRefresh) {
      setupRefreshInterval();
    } else {
      clearRefreshInterval();
    }
  }
  
  // Setup refresh interval
  function setupRefreshInterval() {
    clearRefreshInterval();
    refreshInterval = window.setInterval(() => {
      updateLogs();
    }, 2000);
  }
  
  // Clear refresh interval
  function clearRefreshInterval() {
    if (refreshInterval) {
      window.clearInterval(refreshInterval);
      refreshInterval = undefined;
    }
  }
  
  // Get CSS class for log level
  function getLogLevelClass(level: LogLevel) {
    switch (level) {
      case LogLevel.ERROR:
        return 'text-danger';
      case LogLevel.WARNING:
        return 'text-warning';
      case LogLevel.INFO:
        return 'text-info';
      default:
        return '';
    }
  }
  
  // Format timestamp
  function formatTimestamp(timestamp: string) {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  }
  
  // Format data size for display
  function formatDataSize(bytes: number): string {
    if (!bytes) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  // Get API endpoint from URL
  function getEndpoint(url: string): string {
    try {
      // Extract path from URL
      const urlObj = new URL(url, window.location.origin);
      const pathParts = urlObj.pathname.split('/');
      
      // Return the first part of the path (e.g., /api/timeseries -> /api)
      if (pathParts.length >= 2) {
        return `${pathParts[1]}${pathParts.length >= 3 ? '/' + pathParts[2] : ''}`;
      }
      
      return url;
    } catch {
      // If URL parsing fails, just return the original URL
      return url;
    }
  }
  
  // Open log details modal
  function openLogDetails(log: LogEntry) {
    selectedLog = log;
    showDetailsModal = true;
  }
  
  // Close log details modal
  function closeLogDetails() {
    showDetailsModal = false;
    selectedLog = null;
  }
  
  // Format JSON for display
  function formatJson(data: any): string {
    return JSON.stringify(data, null, 2);
  }
  
  // Format duration in milliseconds
  function formatDuration(ms: number): string {
    if (!ms) return '';
    return `${ms} ms`;
  }
  
  // Get HTTP status class
  function getStatusClass(status: number): string {
    if (!status) return '';
    
    if (status >= 200 && status < 300) return 'text-success';
    if (status >= 300 && status < 400) return 'text-info';
    if (status >= 400 && status < 500) return 'text-warning';
    if (status >= 500) return 'text-danger';
    
    return '';
  }
  
  // Get HTTP method class
  function getMethodClass(method: string): string {
    switch (method?.toUpperCase()) {
      case 'GET': return 'text-success';
      case 'POST': return 'text-primary';
      case 'PUT': return 'text-info';
      case 'DELETE': return 'text-danger';
      case 'PATCH': return 'text-warning';
      default: return '';
    }
  }
  
  // Extract unique endpoints from logs
  function getUniqueEndpoints(): string[] {
    const endpoints = new Set<string>();
    
    logs.forEach(log => {
      if (log.data?.url) {
        const endpoint = getEndpoint(log.data.url);
        endpoints.add(endpoint);
      }
    });
    
    return Array.from(endpoints).sort();
  }
  
  // Export logs to JSON file
  function exportLogs() {
    try {
      const data = JSON.stringify(logs, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link and trigger click
      const a = document.createElement('a');
      a.href = url;
      a.download = `api-logs-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export logs:', error);
    }
  }
  
  // Component lifecycle
  onMount(() => {
    updateLogs();
    setupRefreshInterval();
  });
  
  onDestroy(() => {
    clearRefreshInterval();
  });
</script>

<div class="api-logs-viewer">
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h5 class="mb-0">{t('API Request Logs')}</h5>
    <div class="btn-group">
      <button 
        type="button" 
        class="btn btn-sm btn-outline-secondary" 
        on:click={updateLogs}
      >
        <i class="bi bi-arrow-repeat me-1"></i>
        {t('Refresh')}
      </button>
      <button 
        type="button" 
        class="btn btn-sm btn-outline-secondary" 
        on:click={toggleAutoRefresh}
      >
        <i class={autoRefresh ? "bi bi-pause-circle me-1" : "bi bi-play-circle me-1"}></i>
        {autoRefresh ? t('Auto-refresh: On') : t('Auto-refresh: Off')}
      </button>
      <button 
        type="button" 
        class="btn btn-sm btn-outline-secondary" 
        on:click={exportLogs}
      >
        <i class="bi bi-download me-1"></i>
        {t('Export')}
      </button>
      <button 
        type="button" 
        class="btn btn-sm btn-outline-danger" 
        on:click={clearLogs}
      >
        <i class="bi bi-trash3 me-1"></i>
        {t('Clear')}
      </button>
    </div>
  </div>
  
  <div class="row mb-3">
    <div class="col-md-4 mb-2 mb-md-0">
      <select class="form-select form-select-sm" bind:value={filter}>
        <option value="ALL">{t('All Levels')}</option>
        <option value={LogLevel.INFO}>{t('Info')}</option>
        <option value={LogLevel.WARNING}>{t('Warning')}</option>
        <option value={LogLevel.ERROR}>{t('Error')}</option>
      </select>
    </div>
    
    <div class="col-md-4 mb-2 mb-md-0">
      <select class="form-select form-select-sm" bind:value={endpointFilter}>
        <option value="">{t('All Endpoints')}</option>
        {#each getUniqueEndpoints() as endpoint}
          <option value={endpoint}>{endpoint}</option>
        {/each}
      </select>
    </div>
    
    <div class="col-md-4">
      <div class="input-group input-group-sm">
        <span class="input-group-text">
          <i class="bi bi-search"></i>
        </span>
        <input 
          type="text" 
          class="form-control" 
          placeholder={t('Search logs...')} 
          bind:value={searchTerm}
        >
        {#if searchTerm}
          <button 
            class="btn btn-outline-secondary" 
            type="button"
            on:click={() => searchTerm = ''}
          >
            <i class="bi bi-x"></i>
          </button>
        {/if}
      </div>
    </div>
  </div>
  
  <div class="logs-container">
    {#if getFilteredLogs().length === 0}
      <div class="text-center text-muted p-4">
        <i class="bi bi-info-circle me-2"></i>
        {t('No logs to display')}
      </div>
    {:else}
      <table class="table table-sm table-striped table-hover">
        <thead>
          <tr>
            <th>{t('Time')}</th>
            <th>{t('Level')}</th>
            <th>{t('Method')}</th>
            <th>{t('Endpoint')}</th>
            <th>{t('Status')}</th>
            <th>{t('Duration')}</th>
            <th>{t('Size')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each getFilteredLogs().reverse() as log}
            <tr class="log-row" on:click={() => openLogDetails(log)}>
              <td class="text-nowrap">
                {formatTimestamp(log.timestamp)}
              </td>
              <td class="text-nowrap">
                <span class={getLogLevelClass(log.level)}>
                  {log.level}
                </span>
              </td>
              <td class="text-nowrap">
                {#if log.data?.method}
                  <span class={getMethodClass(log.data.method)}>
                    {log.data.method}
                  </span>
                {/if}
              </td>
              <td>
                {#if log.data?.url}
                  <span class="text-truncate d-inline-block" style="max-width: 150px;" title={log.data.url}>
                    {getEndpoint(log.data.url)}
                  </span>
                {/if}
              </td>
              <td>
                {#if log.data?.status}
                  <span class={getStatusClass(log.data.status)}>
                    {log.data.status}
                  </span>
                {/if}
              </td>
              <td>
                {log.data?.duration ? formatDuration(log.data.duration) : ''}
              </td>
              <td>
                {log.data?.responseSize ? formatDataSize(log.data.responseSize) : ''}
              </td>
              <td>
                <button 
                  class="btn btn-sm btn-link p-0" 
                  title={t('View Details')}
                  on:click|stopPropagation={() => openLogDetails(log)}
                >
                  <i class="bi bi-info-circle"></i>
                </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  </div>
</div>

<!-- Log Details Modal -->
{#if showDetailsModal && selectedLog}
  <div class="modal fade show" style="display: block;" tabindex="-1" aria-modal="true" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {t('Log Details')} - {formatTimestamp(selectedLog.timestamp)}
          </h5>
          <button type="button" class="btn-close" on:click={closeLogDetails} aria-label={t('Close')}></button>
        </div>
        <div class="modal-body">
          <!-- Request Info -->
          <div class="card mb-3">
            <div class="card-header d-flex justify-content-between">
              <h6 class="mb-0">{t('Request Information')}</h6>
              <span class={getLogLevelClass(selectedLog.level)}>{selectedLog.level}</span>
            </div>
            <div class="card-body">
              <!-- Message -->
              <div class="mb-3">
                <label class="form-label fw-bold">{t('Message')}:</label>
                <div>{selectedLog.message}</div>
              </div>
              
              <!-- Request ID -->
              {#if selectedLog.data?.requestId}
                <div class="mb-3">
                  <label class="form-label fw-bold">{t('Request ID')}:</label>
                  <div class="text-monospace">{selectedLog.data.requestId}</div>
                </div>
              {/if}
              
              <!-- HTTP Method & URL -->
              {#if selectedLog.data?.method && selectedLog.data?.url}
                <div class="mb-3">
                  <label class="form-label fw-bold">{t('HTTP Request')}:</label>
                  <div>
                    <span class={getMethodClass(selectedLog.data.method)}>
                      {selectedLog.data.method}
                    </span>
                    <span class="ms-2">{selectedLog.data.url}</span>
                  </div>
                </div>
              {/if}
              
              <!-- Headers -->
              {#if selectedLog.data?.headers}
                <div class="mb-3">
                  <label class="form-label fw-bold">{t('Headers')}:</label>
                  <pre class="mb-0 bg-light p-2 rounded"><code>{formatJson(selectedLog.data.headers)}</code></pre>
                </div>
              {/if}
              
              <!-- Request Body -->
              {#if selectedLog.data?.body}
                <div class="mb-3">
                  <label class="form-label fw-bold">{t('Request Body')}:</label>
                  <pre class="mb-0 bg-light p-2 rounded"><code>{formatJson(selectedLog.data.body)}</code></pre>
                </div>
              {/if}
            </div>
          </div>
          
          <!-- Response Info -->
          {#if selectedLog.data?.status || selectedLog.data?.responseSize || selectedLog.data?.responseData}
            <div class="card mb-3">
              <div class="card-header">
                <h6 class="mb-0">{t('Response Information')}</h6>
              </div>
              <div class="card-body">
                <!-- Status -->
                {#if selectedLog.data?.status}
                  <div class="mb-3">
                    <label class="form-label fw-bold">{t('Status')}:</label>
                    <div>
                      <span class={getStatusClass(selectedLog.data.status)}>
                        {selectedLog.data.status} {selectedLog.data?.statusText || ''}
                      </span>
                    </div>
                  </div>
                {/if}
                
                <!-- Duration -->
                {#if selectedLog.data?.duration}
                  <div class="mb-3">
                    <label class="form-label fw-bold">{t('Duration')}:</label>
                    <div>{formatDuration(selectedLog.data.duration)}</div>
                  </div>
                {/if}
                
                <!-- Response Size -->
                {#if selectedLog.data?.responseSize}
                  <div class="mb-3">
                    <label class="form-label fw-bold">{t('Response Size')}:</label>
                    <div>{formatDataSize(selectedLog.data.responseSize)}</div>
                  </div>
                {/if}
                
                <!-- Response Data Summary -->
                {#if selectedLog.data?.dataSummary}
                  <div class="mb-3">
                    <label class="form-label fw-bold">{t('Response Data Summary')}:</label>
                    <pre class="mb-0 bg-light p-2 rounded"><code>{formatJson(selectedLog.data.dataSummary)}</code></pre>
                  </div>
                {/if}
                
                <!-- Full Response Data (only for errors) -->
                {#if selectedLog.level === LogLevel.ERROR && selectedLog.data?.responseData}
                  <div class="mb-3">
                    <label class="form-label fw-bold">{t('Response Data')}:</label>
                    <pre class="mb-0 bg-light p-2 rounded max-height-200"><code>{formatJson(selectedLog.data.responseData)}</code></pre>
                  </div>
                {/if}
                
                <!-- Error Details -->
                {#if selectedLog.data?.error}
                  <div class="mb-3">
                    <label class="form-label fw-bold">{t('Error')}:</label>
                    <div class="text-danger">{selectedLog.data.error}</div>
                  </div>
                {/if}
                
                <!-- Stack Trace -->
                {#if selectedLog.data?.stack}
                  <div>
                    <label class="form-label fw-bold">{t('Stack Trace')}:</label>
                    <pre class="mb-0 bg-light p-2 rounded max-height-200"><code>{selectedLog.data.stack}</code></pre>
                  </div>
                {/if}
              </div>
            </div>
          {/if}
          
          <!-- Raw Data -->
          <div class="card">
            <div class="card-header d-flex align-items-center">
              <h6 class="mb-0">{t('Raw Log Data')}</h6>
            </div>
            <div class="card-body">
              <pre class="mb-0 bg-light p-2 rounded max-height-300"><code>{formatJson(selectedLog)}</code></pre>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" on:click={closeLogDetails}>
            {t('Close')}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade show"></div>
  </div>
{/if}

<style>
  .api-logs-viewer {
    font-size: 0.875rem;
  }
  
  .logs-container {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
  }
  
  table {
    margin-bottom: 0;
  }
  
  th {
    position: sticky;
    top: 0;
    background-color: #f8f9fa;
    z-index: 1;
  }
  
  .log-row {
    cursor: pointer;
  }
  
  .log-row:hover {
    background-color: rgba(0, 123, 255, 0.05) !important;
  }
  
  pre {
    margin-bottom: 0;
    white-space: pre-wrap;
    font-size: 0.8rem;
  }
  
  code {
    color: inherit;
  }
  
  .max-height-200 {
    max-height: 200px;
    overflow-y: auto;
  }
  
  .max-height-300 {
    max-height: 300px;
    overflow-y: auto;
  }
  
  .text-monospace {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.8rem;
  }
</style>